import { useEffect, useLayoutEffect, useRef } from 'react';
import { MChatConversation } from '@/src/modules/chat/models/chat-conversation';

interface IUseChatConversationListScrollParams {
  scrollAreaRef: React.RefObject<HTMLDivElement | null>;
  onScrollToBottom: () => void;
  isLoadingMore?: boolean;
  isRefreshing?: boolean;
  isReachingEnd?: boolean;
  conversations: MChatConversation[];
  loadMoreConversationThreshold?: number;
  refreshConversations: () => void;
}

const autoRefreshConversationsInterval = 300000; // 5 minutes

export function useChatConversationListScroll({
  scrollAreaRef,
  onScrollToBottom,
  isLoadingMore,
  isRefreshing,
  isReachingEnd,
  conversations,
  loadMoreConversationThreshold = 0,
  refreshConversations,
}: IUseChatConversationListScrollParams) {
  const lastScrollHeightRef = useRef<number>(0);
  const didScrollRef = useRef<boolean>(false);

  const setCurrentLastScrollHeight = () => {
    if (!scrollAreaRef.current) {
      return;
    }
    lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
  };

  const handleScrollReachBottom = () => {
    if (!scrollAreaRef.current) {
      return;
    }

    const actualScrollHeight =
      scrollAreaRef.current.scrollHeight - scrollAreaRef.current.offsetHeight;
    if (
      scrollAreaRef.current.scrollTop >= actualScrollHeight &&
      !isLoadingMore &&
      !isRefreshing &&
      !isReachingEnd
    ) {
      lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
      onScrollToBottom();
    }
  };

  useEffect(() => {
    if (!scrollAreaRef.current) {
      return;
    }
    scrollAreaRef.current.scrollTo({ top: 0 });
  }, [scrollAreaRef.current]);

  useLayoutEffect(() => {
    if (!scrollAreaRef.current || isLoadingMore || isRefreshing) {
      return;
    }

    if (scrollAreaRef.current.scrollTop <= loadMoreConversationThreshold) {
      const scrollHeightDifference =
        scrollAreaRef.current.scrollHeight - lastScrollHeightRef.current;
      scrollAreaRef.current.scrollTop = scrollHeightDifference;
      lastScrollHeightRef.current = scrollAreaRef.current.scrollHeight;
    }
  }, [conversations]);

  useEffect(() => {
    const handleOnScroll = () => (didScrollRef.current = true);

    scrollAreaRef.current?.addEventListener('scroll', handleOnScroll);
    return () => {
      scrollAreaRef.current?.removeEventListener('scroll', handleOnScroll);
    };
  }, []);

  useEffect(() => {
    const refreshConversationsOnFocus = setInterval(() => {
      if (document.hidden || didScrollRef.current) {
        didScrollRef.current = false;
        return;
      }
      refreshConversations();
    }, autoRefreshConversationsInterval);

    return () => clearInterval(refreshConversationsOnFocus);
  }, []);

  return {
    setCurrentLastScrollHeight,
    handleScrollReachBottom,
  };
}
